import React from 'react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Badge } from '../ui/badge';
import { Download, Calendar, Clock, AlertCircle, CheckCircle } from 'lucide-react';

export const EmployeeViewModal = ({
  isOpen,
  onClose,
  employee,
  getDepartmentName,
  formatDate,
  capitalizeFirst,
  getStatusBadgeClass,
  attendanceHistory,
  loadingAttendance,
  onDownloadYearlyAttendance,
  formatCurrency
}) => {
  if (!employee) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="employee-view-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl flex items-center gap-3">
            {employee.name}
            <span className={`text-sm px-2.5 py-1 rounded-full ${getStatusBadgeClass(employee.status)}`}>
              {capitalizeFirst(employee.status)}
            </span>
          </DialogTitle>
        </DialogHeader>

        <Tabs defaultValue="details" className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="details">Employee Details</TabsTrigger>
            <TabsTrigger value="salary">Salary Structure</TabsTrigger>
            <TabsTrigger value="attendance">Recent Attendance</TabsTrigger>
          </TabsList>

          <TabsContent value="details" className="space-y-6 mt-4">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-x-8 gap-y-4">
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider">Employee Code</p>
                <p className="font-medium text-slate-900">{employee.employee_code}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider">Email</p>
                <p className="font-medium text-slate-900">{employee.email}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider">Department</p>
                <p className="font-medium text-slate-900">{getDepartmentName(employee.department_id)}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider">Designation</p>
                <p className="font-medium text-slate-900">{employee.designation || 'N/A'}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider">Date of Joining</p>
                <p className="font-medium text-slate-900">{formatDate(employee.date_of_joining)}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider">Employment Type</p>
                <p className="font-medium text-slate-900">{capitalizeFirst(employee.employment_type)}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider">Pay Scale / Grade</p>
                <p className="font-medium text-slate-900">{employee.pay_scale || 'N/A'} / {employee.grade || 'N/A'}</p>
              </div>
            </div>

            {/* Bank Details */}
            <div className="border-t pt-4">
              <h4 className="text-sm font-semibold text-slate-700 mb-3">Bank Details</h4>
              <div className="grid grid-cols-2 gap-x-8 gap-y-4">
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wider">Bank Name</p>
                  <p className="font-medium text-slate-900">{employee.bank_name || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wider">Account Number</p>
                  <p className="font-medium text-slate-900">{employee.bank_account_number || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wider">IFSC Code</p>
                  <p className="font-medium text-slate-900">{employee.ifsc_code || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wider">Branch</p>
                  <p className="font-medium text-slate-900">{employee.bank_branch || 'N/A'}</p>
                </div>
              </div>
            </div>

            {/* Identity Documents */}
            <div className="border-t pt-4">
              <h4 className="text-sm font-semibold text-slate-700 mb-3">Identity Documents</h4>
              <div className="grid grid-cols-2 gap-x-8 gap-y-4">
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wider">PAN Number</p>
                  <p className="font-medium text-slate-900">{employee.pan_number || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wider">Aadhaar Number</p>
                  <p className="font-medium text-slate-900">{employee.aadhaar_number || 'N/A'}</p>
                </div>
              </div>
            </div>

            {/* GPA/Insurance */}
            {employee.gpa_policy_number && (
              <div className="border-t pt-4">
                <h4 className="text-sm font-semibold text-slate-700 mb-3">GPA / Insurance</h4>
                <div className="grid grid-cols-2 gap-x-8 gap-y-4">
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wider">Policy Number</p>
                    <p className="font-medium text-slate-900">{employee.gpa_policy_number}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wider">Coverage Amount</p>
                    <p className="font-medium text-slate-900">{formatCurrency ? formatCurrency(employee.gpa_coverage_amount) : `₹${employee.gpa_coverage_amount?.toLocaleString()}`}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wider">Nominee</p>
                    <p className="font-medium text-slate-900">
                      {employee.gpa_nominee_name} ({employee.gpa_nominee_relationship})
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wider">Validity</p>
                    <p className="font-medium text-slate-900">
                      {formatDate(employee.gpa_validity_start)} - {formatDate(employee.gpa_validity_end)}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </TabsContent>

          <TabsContent value="salary" className="mt-4">
            <div className="bg-slate-50 rounded-lg p-6">
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-4">
                  <h4 className="font-semibold text-slate-700 flex items-center gap-2">
                    <span className="w-2 h-2 bg-emerald-500 rounded-full"></span>
                    Earnings
                  </h4>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-slate-600">Basic Salary</span>
                      <span className="font-medium">₹{(employee.basic_salary || 0).toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">HRA</span>
                      <span className="font-medium">₹{(employee.hra || 0).toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">DA</span>
                      <span className="font-medium">₹{(employee.da || 0).toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Special Allowance</span>
                      <span className="font-medium">₹{(employee.special_allowance || 0).toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Conveyance</span>
                      <span className="font-medium">₹{(employee.conveyance || 0).toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Medical Allowance</span>
                      <span className="font-medium">₹{(employee.medical_allowance || 0).toLocaleString()}</span>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <h4 className="font-semibold text-slate-700">Summary</h4>
                  <div className="bg-white rounded-lg p-4 space-y-3">
                    <div className="flex justify-between text-lg">
                      <span className="text-slate-600">Gross Salary</span>
                      <span className="font-bold text-emerald-600">
                        ₹{((employee.basic_salary || 0) + (employee.hra || 0) + (employee.da || 0) + 
                           (employee.special_allowance || 0) + (employee.conveyance || 0) + 
                           (employee.medical_allowance || 0)).toLocaleString()}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="attendance" className="mt-4">
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-semibold text-slate-700">Last 7 Days Attendance</h4>
              <Button variant="outline" size="sm" onClick={onDownloadYearlyAttendance}>
                <Download className="w-4 h-4 mr-2" />
                Download Yearly Report
              </Button>
            </div>

            {loadingAttendance ? (
              <div className="flex items-center justify-center py-8">
                <div className="spinner w-6 h-6"></div>
              </div>
            ) : attendanceHistory?.attendance?.length > 0 ? (
              <div className="space-y-2">
                {attendanceHistory.attendance.map((record, index) => (
                  <div
                    key={record.date || `attendance-${index}`}
                    className={`flex items-center justify-between p-3 rounded-lg border ${
                      record.status === 'present' ? 'bg-emerald-50 border-emerald-200' :
                      record.status === 'absent' ? 'bg-red-50 border-red-200' :
                      record.status === 'half_day' ? 'bg-amber-50 border-amber-200' :
                      record.status === 'leave' ? 'bg-blue-50 border-blue-200' :
                      'bg-slate-50 border-slate-200'
                    }`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="text-center min-w-[60px]">
                        <p className="text-xs text-slate-500">{new Date(record.date).toLocaleDateString('en-US', { weekday: 'short' })}</p>
                        <p className="font-semibold">{new Date(record.date).getDate()}</p>
                      </div>
                      <div>
                        <Badge className={
                          record.status === 'present' ? 'bg-emerald-100 text-emerald-700' :
                          record.status === 'absent' ? 'bg-red-100 text-red-700' :
                          record.status === 'half_day' ? 'bg-amber-100 text-amber-700' :
                          record.status === 'leave' ? 'bg-blue-100 text-blue-700' :
                          'bg-slate-100 text-slate-700'
                        }>
                          {record.status?.replace('_', ' ').toUpperCase() || 'N/A'}
                        </Badge>
                      </div>
                    </div>
                    <div className="flex items-center gap-6 text-sm">
                      <div className="flex items-center gap-1 text-slate-600">
                        <Clock className="w-4 h-4" />
                        <span>{record.check_in_time || '--:--'}</span>
                        <span className="text-slate-400">-</span>
                        <span>{record.check_out_time || '--:--'}</span>
                      </div>
                      {record.location_verified !== undefined && (
                        <div className="flex items-center gap-1">
                          {record.location_verified ? (
                            <CheckCircle className="w-4 h-4 text-emerald-500" />
                          ) : (
                            <AlertCircle className="w-4 h-4 text-amber-500" />
                          )}
                          <span className="text-xs text-slate-500">
                            {record.location_verified ? 'Location OK' : 'Location N/A'}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-500">
                <Calendar className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                <p>No attendance records for this period</p>
              </div>
            )}

            {attendanceHistory?.summary && (
              <div className="grid grid-cols-4 gap-4 mt-6 pt-4 border-t">
                <div className="text-center">
                  <p className="text-2xl font-bold text-emerald-600">{attendanceHistory.summary.present || 0}</p>
                  <p className="text-xs text-slate-500">Present</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-red-600">{attendanceHistory.summary.absent || 0}</p>
                  <p className="text-xs text-slate-500">Absent</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-amber-600">{attendanceHistory.summary.half_day || 0}</p>
                  <p className="text-xs text-slate-500">Half Day</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-blue-600">{attendanceHistory.summary.leave || 0}</p>
                  <p className="text-xs text-slate-500">Leave</p>
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

export default EmployeeViewModal;
